/**
 * CollapsibleSection
 *
 * Reusable expand/collapse section for the Three-Panel Console center panel.
 * Each section can be default-expanded or collapsed with a summary line.
 *
 * Uses IntersectionObserver for lazy loading — collapsed sections only mount
 * their children when first scrolled into view (PRD: performance optimization).
 *
 * Cumulus Spec 7B: 48px headers, 200ms expand/collapse, role-based defaults.
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CollapsibleSectionProps {
  title: string;
  icon?: React.ReactNode;
  defaultExpanded?: boolean;
  summary?: string;
  children: React.ReactNode;
  className?: string;
  /** Optional badge count shown next to the title */
  badge?: number;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  defaultExpanded = false,
  summary,
  children,
  className,
  badge,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [hasBeenVisible, setHasBeenVisible] = useState(defaultExpanded);
  const contentRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Lazy-load: observe when section scrolls into viewport
  useEffect(() => {
    if (hasBeenVisible) return;

    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasBeenVisible]);

  return (
    <div
      ref={sectionRef}
      className={cn('rounded-lg border border-slate-200 bg-white', className)}
    >
      {/* 48px header per Cumulus spec 7B */}
      <button
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between px-4 h-12 text-left hover:bg-slate-50 transition-colors duration-150 rounded-t-lg"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          {badge !== undefined && badge > 0 && (
            <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-slate-100 text-[10px] font-medium text-slate-600">
              {badge}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isExpanded && summary && (
            <span className="text-xs text-slate-400">{summary}</span>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-slate-400 transition-transform duration-200',
              !isExpanded && '-rotate-90'
            )}
          />
        </div>
      </button>

      {/* 200ms animated expand/collapse per Cumulus spec 7B */}
      <div
        ref={contentRef}
        className={cn(
          'overflow-hidden transition-all duration-200 ease-in-out',
          isExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="border-t border-slate-100 px-4 py-3">
          {hasBeenVisible ? (
            children
          ) : (
            <div className="flex items-center justify-center py-6 text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
